import {
  FormControl,
  FormLabel,
  Box,
  Typography,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  DateValidationError,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useMemo } from "react";
import { ChangeEventDatePicker } from "../../../core/models/bills/bills.model";
import {
  DatePickersComponentProps,
  DatePickersData,
} from "../../../core/models/datePickersComponent/datePickersComponent.model";
// @ts-ignore SCSS is loaded by the bundler and has no TypeScript declarations.
import "./DatePickersComponent.scss";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { isEmpty, isUndefined } from "lodash";

const DatePickersComponent = ({
  filters,
  setFilters,
  justifyContent = "space-between",
  billsSearchBar,
  customTitle,
  required,
  noWrap,
  enableFuture,
  label,
  error,
  setError,
  maxDateTo,
  customDateError,
  totalOptionIsChecked,
  setTotalOptionIsChecked,
}: DatePickersComponentProps) => {
  if (
    !isUndefined(totalOptionIsChecked) &&
    isUndefined(setTotalOptionIsChecked)
  ) {
    throw new Error(
      "DatePickersComponent Error: If 'totalOptionIsChecked' is provided, 'setTotalOptionIsChecked' must also be provided.",
    );
  }

  const { t } = useTranslation();
  const isDesktopViewport = useMediaQuery("(min-width:744px)");
  const isSmallViewport = useMediaQuery("(max-width:944px)");
  const datePickersData: DatePickersData[] = [
    { id: "dateFrom", label: "Fecha inicio", minDate: false },
    {
      id: "dateTo",
      label: "Fecha fin",
      minDate: true,
    },
  ];

  const isDateFromValid = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // set to end of today for comparison
    let dateFromValue = filters?.dateFrom?.$d;

    if (filters?.dateFrom === null || !dateFromValue) {
      return false;
    }

    // Check if dateFromValue is actually a Date object
    if (!(dateFromValue instanceof Date)) {
      dateFromValue = new Date(dateFromValue);
    }

    // check if the selected date is in the future
    if (!enableFuture && dateFromValue > today) {
      return false;
    }

    // check if it's a valid date
    if (isNaN(dateFromValue.getTime())) {
      return false;
    }

    return true;
  }, [filters?.dateFrom, enableFuture]);

  useEffect(() => {
    if (!isDateFromValid && error?.dateTo === "minDate") {
      setError((prev) => ({
        ...prev,
        dateTo: "",
      }));
    }
  }, [isDateFromValid, error?.dateTo, setError]);

  const errorMessage = (errors: DateValidationError) => {
    if (customDateError) {
      for (let err of customDateError) {
        switch (errors) {
          case err.errorType: {
            return err.text;
          }
        }
      }
    }

    switch (errors) {
      case "disableFuture":
      case "disablePast":
      case "invalidDate":
      case "minDate":
      case "maxDate": {
        return t("Datepicker.Introduzca una fecha válida");
      }

      default: {
        return "";
      }
    }
  };
  return (
    <FormControl
      className="form-stack"
      sx={{
        display: "flex",
        gap: ".85rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <FormLabel>
          <Typography fontWeight={600}>
            {customTitle && !isEmpty(customTitle)
              ? t(customTitle)
              : t(`Tablas.${label ? label : "Periodo de facturación"}`)}
            {required && (
              <Typography component={"span"} color={"red"}>
                {" "}
                *{" "}
              </Typography>
            )}
          </Typography>
        </FormLabel>
        {!isUndefined(totalOptionIsChecked) && (
          <Box
            sx={{
              ml: 5,
              cursor: "pointer !important",
              gap: "0.5rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Checkbox
              id="date-picker-total-checkbox"
              checked={totalOptionIsChecked}
              onChange={(e) => {
                setTotalOptionIsChecked!(e.target.checked);
              }}
              aria-label={t("Total period")}
            />
            <FormLabel htmlFor="date-picker-total-checkbox">
              {t("Total period")}
            </FormLabel>
          </Box>
        )}
      </Box>

      <Box
        width={`${billsSearchBar || noWrap ? "100%" : "auto"}`}
        className={`datepicker-wrapper ${isSmallViewport && "small-screen"}`}
        component="section"
        display="flex"
        justifyContent={isDesktopViewport ? justifyContent : "flex-start"}
        alignItems="center"
        flexWrap={`${billsSearchBar || noWrap ? "nowrap" : "wrap"}`}
        gap={isDesktopViewport ? "0.5rem" : "1rem"}
      >
        {datePickersData.map(({ id, label, minDate }) => (
          <React.Fragment key={id}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                disabled={totalOptionIsChecked}
                maxDate={(() => {
                  if (id === "dateTo" && maxDateTo) {
                    // Try to extract the actual date value
                    let dateValue: Date | string | number | null | undefined;
                    if (maxDateTo?.$d) {
                      dateValue = maxDateTo.$d;
                    } else if (maxDateTo instanceof Date) {
                      dateValue = maxDateTo as Date;
                    } else {
                      dateValue = maxDateTo as any;
                    }

                    const maxDateValue = dayjs(dateValue);
                    return maxDateValue.isValid() ? maxDateValue : undefined;
                  }
                  return undefined;
                })()}
                minDate={
                  id === "dateTo" &&
                  minDate &&
                  isDateFromValid &&
                  filters.dateFrom
                    ? dayjs(filters.dateFrom.$d)
                    : undefined
                }
                disableFuture={enableFuture ? false : true}
                onChange={(event) => {
                  // Convert Dayjs back to ChangeEventDatePicker format
                  const convertedEvent = event ? { $d: event.toDate() } : null;
                  setFilters(
                    id,
                    convertedEvent as ChangeEventDatePicker | null,
                  );
                }}
                onError={(newError) => {
                  if (
                    id === "dateTo" &&
                    newError === "minDate" &&
                    !isDateFromValid
                  ) {
                    return;
                  }

                  setError({
                    ...error,
                    [id]: newError === null ? "" : newError,
                  });
                }}
                value={filters?.[id] ? dayjs(filters[id]!.$d) : null}
                slotProps={{
                  textField: {
                    error: !!error?.[id],
                    helperText: (() => {
                      const errorText = error?.[id]
                        ? errorMessage(error[id] as DateValidationError)
                        : "";
                      return errorText;
                    })(),
                    InputProps: {
                      id: id,
                      placeholder: t(`Tablas.${label}`),
                      required: required ? true : false,
                      sx: {
                        width: `${billsSearchBar || noWrap ? "100%" : "11rem"}`,
                      },
                      // startAdornment: filters[id] && (
                      //   <IconButton
                      //     sx={{ position: "absolute", right: "2rem", backgroundColor: theme.palette.background.default }}
                      //     onClick={() => setFilters(id, null)}
                      //   >
                      //     <span className="t-icon-close" />
                      //   </IconButton>
                      // ),
                    },
                  },
                }}
              />
            </LocalizationProvider>
            {!minDate && (
              <Typography
                component="p"
                // display="flex"
                // paddingTop="16px"
                variant="body1"
              >
                {t("Tablas.Al")}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
    </FormControl>
  );
};

export default DatePickersComponent;
